<template>
    <TprmModal
        open
        title="Email Notification Details"
        subtitle="View send details for the following email."
        size="md"
        allow-close
        @close="$emit('abort')"
    >
        <div class="ViewNotificationDetailProcedure">
            <div
                v-for="section in sections"
                :key="section.label"
                class="ViewNotificationDetailProcedure__section"
            >
                <div class="ViewNotificationDetailProcedure__sectionLabel">
                    {{ section.label }}
                </div>
                <div class="ViewNotificationDetailProcedure__value">
                    {{ section.value }}
                </div>
            </div>
        </div>
        <template #footer>
            <NewButton variant="outline" color="neutral" @click="$emit('abort')">
                Close
            </NewButton>
        </template>
    </TprmModal>
</template>

<style lang="scss">
.ViewNotificationDetailProcedure {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__section {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__sectionLabel {
        font-size: var(--text-label-size, 12px);
        font-weight: var(--text-label-weight, 600);
        color: var(--eidSecureBlue-50);
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }

    &__value {
        font-size: 14px;
        color: var(--primary-font-color);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { NotificationDetails, NotificationEventType } from '@evidentid/tprm-portal-lib/models/activity-history';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    const DELIVERY_STATUS_LABELS: Record<string, string> = {
        [NotificationEventType.SENT]: 'Sent',
        [NotificationEventType.REJECTED]: 'Rejected',
        [NotificationEventType.BOUNCE]: 'Bounced',
        [NotificationEventType.SPAM]: 'Marked as Spam',
    };

    const EMAIL_TYPE_LABELS: Record<string, string> = {
        INITIAL: 'Initial Request',
        REMINDER: 'Reminder',
    };

    // PRODUCT-26110 QA fixes4: declares the `abort` event this procedure component
    // `$emit`s -- without a declared `emits`, Vue 3 warns "Component emitted event
    // 'abort' but it is neither declared in the emits option nor as an 'onAbort' prop"
    // every time it fires.
    export default defineComponent({
        name: 'ViewNotificationDetailProcedure',
        components: { TprmModal, NewButton },
        props: {
            // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't
            // warn "Extraneous non-props attributes (uuid)" (see
            // UploadEntityDocumentProcedure.vue).
            uuid: {
                type: String,
                default: '',
            },
            details: {
                type: Object as PropType<NotificationDetails>,
                required: true,
            },
        },
        emits: [ 'abort' ],
        computed: {
            emailTypeLabel(): string {
                const { notificationOperation } = this.details;
                return EMAIL_TYPE_LABELS[notificationOperation] || notificationOperation;
            },
            deliveryStatusLabel(): string {
                return DELIVERY_STATUS_LABELS[this.details.eventType] || this.details.eventType;
            },
            sections(): { label: string, value: string }[] {
                return [
                    { label: 'Sent to', value: this.details.recipientEmail },
                    { label: 'CC', value: (this.details.cc ?? []).join(', ') },
                    { label: 'Subject Line', value: this.details.subject || '' },
                    { label: 'Email Type', value: this.emailTypeLabel },
                    { label: 'Delivery Status', value: this.deliveryStatusLabel },
                    { label: 'Message ID', value: this.details.externalNotificationId || '' },
                    { label: 'Send ID', value: this.details.sendId || '' },
                ].filter((section) => Boolean(section.value));
            },
        },
    });
</script>
